import { forwardRef, useMemo, useRef, useState } from "react";
import type { ShowcasePlatformFit } from "../../types.ts";
import { findPlatformRef, LAYERS, MODULES } from "../../data/platform.ts";
import type { PlatformModule, PlatformLayer } from "../../data/platform.ts";
import { SlideEyebrow, SlideHeading } from "../common.tsx";

interface Props {
  index: number;
  appKey?: string;
  platformFit: ShowcasePlatformFit;
}

interface TooltipState {
  name: string;
  desc: string;
  status: string;
  x: number;
  y: number;
  cardH: number;
  flipped: boolean;
}

export const PlatformFitSlide = forwardRef<HTMLElement, Props>(function PlatformFitSlide(
  { index, appKey, platformFit },
  ref
) {
  const frameRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  const touched = useMemo(() => {
    const set = new Set<string>();
    for (const t of platformFit.touches || []) {
      const refObj = findPlatformRef(t);
      if (refObj) set.add(refObj.kind + ":" + refObj.id);
    }
    return set;
  }, [platformFit.touches]);

  function showTooltip(e: React.MouseEvent<HTMLDivElement>, item: PlatformModule | PlatformLayer) {
    const frame = frameRef.current;
    if (!frame) return;
    const frameRect = frame.getBoundingClientRect();
    const cardRect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    const relativeTop = cardRect.top - frameRect.top;
    setTooltip({
      name: item.name,
      desc: item.desc,
      status: item.status,
      x: cardRect.left - frameRect.left + cardRect.width / 2,
      y: relativeTop,
      cardH: cardRect.height,
      flipped: relativeTop < 180,
    });
  }

  function hideTooltip() {
    setTooltip(null);
  }

  return (
    <section ref={ref} className="dz-slide">
      <div className="dz-container">
        <SlideEyebrow index={index} label="Platform Fit" />
        <SlideHeading plain="One platform, many modules." />

        <div className="dz-map-frame" ref={frameRef}>
          {/* Hover tooltip */}
          {tooltip && (
            <div
              className={`dz-map-tooltip${tooltip.flipped ? " dz-map-tooltip--below" : ""}`}
              style={{ left: tooltip.x, top: tooltip.flipped ? tooltip.y + tooltip.cardH : tooltip.y }}
            >
              <div className="dz-map-tooltip__name">{tooltip.name}</div>
              <p className="dz-map-tooltip__desc">{tooltip.desc}</p>
            </div>
          )}

          <div className="dz-map-layer-label">10 Modules</div>
          <div className="dz-map-modules">
            {MODULES.map((m) => {
              const isThis = m.id === appKey;
              const isTouched = !isThis && touched.has("mod:" + m.id);
              const cls = isThis
                ? "dz-map-mod dz-map-mod--this"
                : isTouched
                ? "dz-map-mod dz-map-mod--touched"
                : "dz-map-mod";
              return (
                <div
                  className={cls}
                  key={m.id}
                  onMouseEnter={(e) => showTooltip(e, m)}
                  onMouseLeave={hideTooltip}
                >
                  <div className="dz-map-mod__n">M{m.n}</div>
                  <div className="dz-map-mod__name">{m.name}</div>
                </div>
              );
            })}
          </div>

          <div className="dz-map-layer-label">3 Platform Layers</div>
          <div className="dz-map-layers">
            {LAYERS.map((l) => {
              const isTouched = touched.has("layer:" + l.id);
              return (
                <div
                  className={isTouched ? "dz-map-layer dz-map-layer--touched" : "dz-map-layer"}
                  key={l.id}
                  onMouseEnter={(e) => showTooltip(e, l)}
                  onMouseLeave={hideTooltip}
                >
                  <div className="dz-map-layer__letter">LAYER {l.letter}</div>
                  <div className="dz-map-layer__name">{l.name}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
});

